import { useEffect, useMemo, useRef } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  HStack,
  IconButton,
  Link,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { IoArrowBack } from 'react-icons/io5';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';

import {
  buildComparePath,
  compareEventProps,
  DATASETS_PARAM,
  datasetRefKey,
  encodeCompareDatasets,
  isSameSystemModel,
  parseCompareDatasets,
  useIsDatasetComparisonEnabled,
} from 'components/shared/Compare';
import PageHead from 'components/shared/PageHead';
import AppCard from 'components/UI/AppCard';
import useVersionInfo from 'hooks/useVersionInfo';
import { incrementComparison } from 'utilities/consent/cookies';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import ComparisonCard from './ComparisonCard';
import { CrossReleaseNotice, describeReleases } from './CrossReleaseNotice';
import ExchangesComparison from './ExchangesComparison';
import ImpactAssessmentComparison from './ImpactAssessmentComparison';
import { useResolveCompareDataset } from './useResolveCompareDataset';

const COMPARE_TABS = [
  { slug: 'impact-assessment', label: 'Impact Assessment' },
  { slug: 'exchanges', label: 'Exchanges' },
] as const;

const LEGACY_TAB_SLUGS: Record<string, string> = { flows: 'exchanges' };

const Comparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ver, system_model } = useVersionInfo();
  const [searchParams] = useSearchParams();

  const backArrowColor = useColorModeValue('gray.600', 'gray.400');

  const routeRelease = useMemo(() => ({ version: ver, system_model }), [ver, system_model]);
  const rawDatasets = searchParams.get(DATASETS_PARAM);

  const { refs, invalidEntries, droppedEntries } = useMemo(
    () => parseCompareDatasets(rawDatasets, routeRelease),
    [rawDatasets, routeRelease]
  );

  const searchPath = `/${ver}/${system_model}/search`;
  const comparePath = `/${ver}/${system_model}/compare`;

  const currentSlug = location.pathname.split('/')[4];
  const activeIndex = COMPARE_TABS.findIndex((tab) => tab.slug === currentSlug);
  const isCompareEnabled = useIsDatasetComparisonEnabled();

  const [refA, refB] = [refs[0], refs[1]];

  const { dataset: datasetA, isLoading: isLoadingA } = useResolveCompareDataset(refA);
  const { dataset: datasetB, isLoading: isLoadingB } = useResolveCompareDataset(refB);
  const launch = location.state as { entry?: 'tray' | 'history'; secondsToStart?: number; from?: string } | null;
  const entry = launch?.entry ?? 'direct_link';

  const hasMixedSystemModels = Boolean(refA && refB && !isSameSystemModel(refA, refB));

  const shouldCanonicaliseRoute =
    Boolean(refA) && (refA.version !== ver || refA.systemModel !== system_model) && activeIndex !== -1;

  useEffect(() => {
    if (!isCompareEnabled) return;

    if (shouldCanonicaliseRoute) {
      navigate(buildComparePath(currentSlug, { version: refA.version, system_model: refA.systemModel }, refs), {
        replace: true,
        state: location.state,
      });
      return;
    }

    if (activeIndex === -1) {
      navigate(`${comparePath}/${LEGACY_TAB_SLUGS[currentSlug] ?? COMPARE_TABS[0].slug}${location.search}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [
    isCompareEnabled,
    shouldCanonicaliseRoute,
    activeIndex,
    navigate,
    currentSlug,
    refA,
    refs,
    comparePath,
    location.search,
    location.state,
  ]);

  const trackedViewKeyRef = useRef<string | null>(null);
  // Encoded refs, not ids: the same id in another release is a new view.
  const viewKey = encodeCompareDatasets(refs);

  useEffect(() => {
    if (!isCompareEnabled || !viewKey || activeIndex === -1) return;
    if (isLoadingA || isLoadingB) return;
    if (trackedViewKeyRef.current === viewKey) return;

    trackedViewKeyRef.current = viewKey;
    const failedDatasetIds = refs.filter((_, index) => ![datasetA, datasetB][index]).map((ref) => ref.id);

    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_VIEWED, {
      ...compareEventProps([datasetA, datasetB]),
      dataset_ids: refs.map((ref) => ref.id),
      dataset_count: refs.length,
      dataset_releases: refs.map((ref) => `${ref.version}-${ref.systemModel}`),
      // Overrides the route's super properties: Layout's effect re-registers them only after this one runs.
      version: refs[0].version,
      system_model: refs[0].systemModel,
      failed_dataset_ids: failedDatasetIds,
      tab: currentSlug,
      entry,
      ...(entry === 'tray' && { seconds_to_start: launch?.secondsToStart }),
      ...(hasMixedSystemModels && { blocked_reason: 'system_model_mismatch' }),
    });

    if (refs.length === 2 && failedDatasetIds.length === 0 && !hasMixedSystemModels) incrementComparison();
  }, [
    isCompareEnabled,
    viewKey,
    refs,
    activeIndex,
    isLoadingA,
    isLoadingB,
    datasetA,
    datasetB,
    currentSlug,
    entry,
    launch,
    hasMixedSystemModels,
  ]);

  const onTabClick = (slug: string) => {
    if (slug === currentSlug) return;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_TAB_CHANGED, {
      tab: slug,
      from_tab: currentSlug,
      dataset_ids: refs.map((ref) => ref.id),
    });
  };

  if (!isCompareEnabled) return <Navigate to={searchPath} replace />;

  return (
    <Stack alignItems={'center'} width="100%" spacing={0}>
      <PageHead title="Dataset comparison" />
      <Stack maxWidth={'container.xl'} width={{ base: '100%', md: '95%' }} spacing={4} py={5}>
        <Stack spacing={4} px={{ base: 4, md: 0 }}>
          <Breadcrumb fontSize="sm">
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to={searchPath}>
                Datasets
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage fontWeight="medium">
              <BreadcrumbLink>Dataset comparison</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          <Heading as="h4">Dataset comparison</Heading>
        </Stack>

        <AppCard spacing={6}>
          <HStack spacing={3}>
            <IconButton
              as={RouterLink}
              to={launch?.from ?? searchPath}
              aria-label="Back"
              icon={<IoArrowBack />}
              variant="link"
              fontSize="24px"
              color={backArrowColor}
              minW="auto"
            />
            <Heading size="lg">Comparison</Heading>
          </HStack>

          {invalidEntries.length > 0 && (
            <Alert status="warning" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {invalidEntries.length === 1 ? 'One dataset in this link' : `${invalidEntries.length} datasets in this link`}{' '}
                could not be read and {invalidEntries.length === 1 ? 'was' : 'were'} skipped. A dataset must be identified by
                its id and release.
              </AlertDescription>
            </Alert>
          )}

          {droppedEntries.length > 0 && (
            <Alert status="info" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                Only the first two datasets are compared; {droppedEntries.length} more{' '}
                {droppedEntries.length === 1 ? 'was' : 'were'} ignored.
              </AlertDescription>
            </Alert>
          )}

          {refs.length === 0 ? (
            <Text color="gray.500">
              No datasets selected to compare.{' '}
              <Link as={RouterLink} to={searchPath} color="green.500">
                Go back to search
              </Link>
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              {refs.map((ref) => (
                <ComparisonCard key={datasetRefKey(ref)} datasetRef={ref} />
              ))}
            </SimpleGrid>
          )}

          {hasMixedSystemModels ? (
            <CrossReleaseNotice title="These datasets can't be compared">
              {describeReleases([refA, refB])} use different system models. Cutoff and consequential answer different questions,
              so their scores aren&apos;t directly comparable. Pick two datasets that share a system model.
            </CrossReleaseNotice>
          ) : (
            <Tabs variant="soft-rounded" colorScheme="green" index={Math.max(activeIndex, 0)} isLazy>
              <TabList>
                {COMPARE_TABS.map((tab) => (
                  <Tab
                    key={tab.slug}
                    as={RouterLink}
                    state={location.state}
                    borderRadius="md"
                    to={`${comparePath}/${tab.slug}${location.search}`}
                    onClick={() => onTabClick(tab.slug)}
                  >
                    {tab.label}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <ImpactAssessmentComparison refs={refs} />
                </TabPanel>
                <TabPanel px={0}>
                  <ExchangesComparison refs={refs} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </AppCard>
      </Stack>
    </Stack>
  );
};

export default Comparison;
